import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiCpu, FiMessageSquare, FiEdit, FiBarChart2, FiArrowRight, FiLock,
  FiZap, FiSettings, FiActivity, FiTrendingUp, FiFileText, FiMail,
  FiImage, FiCode, FiCheckCircle, FiClock, FiUsers, FiTarget, FiX,
  FiCheck, FiInfo
} = FiIcons;

const AIIntegration = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [aiConfig, setAiConfig] = useState({
    apiKey: '',
    model: 'gpt-4',
    temperature: 0.7,
    maxTokens: 2000,
    enableAnalytics: true,
    enableChat: true,
    enableContent: false
  });

  const aiFeatures = [
    {
      title: 'AI Chat Assistant',
      description: 'Engage with our intelligent assistant for instant support and information retrieval.',
      icon: FiMessageSquare,
      path: '/ai-chat',
      color: 'blue',
      status: 'active',
      usage: 2847,
      accuracy: 94.5
    },
    {
      title: 'Content Generation',
      description: 'Automate creation of blog posts, social media updates, and patient communications.',
      icon: FiEdit,
      path: '#',
      color: 'purple',
      status: 'beta',
      usage: 1234,
      accuracy: 89.2
    },
    {
      title: 'Analytics Insights',
      description: 'Automatically uncover trends, predict outcomes, and get actionable insights from your data.',
      icon: FiBarChart2,
      path: '#',
      color: 'green',
      status: 'active',
      usage: 3421,
      accuracy: 91.8
    },
    {
      title: 'Smart Recommendations',
      description: 'AI-powered product and service recommendations for patients and providers.',
      icon: FiTarget,
      path: '#',
      color: 'orange',
      status: 'active',
      usage: 1876,
      accuracy: 87.3
    },
    {
      title: 'Email Automation',
      description: 'Generate personalized email campaigns and automate patient communications.',
      icon: FiMail,
      path: '#',
      color: 'red',
      status: 'beta',
      usage: 956,
      accuracy: 92.1
    },
    {
      title: 'Image Processing',
      description: 'Advanced image analysis and processing for dental imaging and documentation.',
      icon: FiImage,
      path: '#',
      color: 'indigo',
      status: 'coming-soon',
      usage: 0,
      accuracy: 0
    }
  ];

  const stats = {
    totalRequests: 10334,
    activeModels: 6,
    avgResponseTime: 0.85,
    costSavings: 34500
  };

  const recentActivity = [
    { type: 'chat', user: 'Dr. Sarah Mitchell', action: 'Used AI Chat for patient inquiry', time: '2 minutes ago' },
    { type: 'content', user: 'Dr. Michael Chen', action: 'Generated blog post draft', time: '15 minutes ago' },
    { type: 'analytics', user: 'System', action: 'Completed weekly trend analysis', time: '1 hour ago' },
    { type: 'recommendation', user: 'Dr. Lisa Rodriguez', action: 'Received treatment recommendations', time: '2 hours ago' }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200', hover: 'hover:border-blue-300' },
      purple: { bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-200', hover: 'hover:border-purple-300' },
      green: { bg: 'bg-green-50', text: 'text-green-600', border: 'border-green-200', hover: 'hover:border-green-300' },
      orange: { bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-200', hover: 'hover:border-orange-300' },
      red: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200', hover: 'hover:border-red-300' },
      indigo: { bg: 'bg-indigo-50', text: 'text-indigo-600', border: 'border-indigo-200', hover: 'hover:border-indigo-300' }
    };
    return colors[color] || colors.blue;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">Active</span>;
      case 'beta':
        return <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">Beta</span>;
      case 'coming-soon':
        return <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs font-medium rounded-full">Coming Soon</span>;
      default:
        return null;
    }
  };

  const handleConfigChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAiConfig(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleConfigSave = (e) => {
    e.preventDefault();
    console.log('AI Configuration saved:', aiConfig);
    // Add your save logic here
    setShowConfigModal(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50 to-purple-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">AI Integration Hub</h1>
              <p className="text-gray-600 mt-1">Leverage the power of artificial intelligence in your practice</p>
            </div>
            <div className="mt-4 sm:mt-0">
              <button 
                onClick={() => setShowConfigModal(true)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center space-x-2"
              >
                <SafeIcon icon={FiSettings} className="w-4 h-4" />
                <span>Configure AI</span>
              </button>
            </div>
          </div>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-indigo-50 rounded-lg">
                <SafeIcon icon={FiActivity} className="w-6 h-6 text-indigo-600" />
              </div>
              <span className="text-sm text-green-600 font-medium">+24%</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.totalRequests.toLocaleString()}</p>
            <p className="text-sm text-gray-600 mt-1">Total AI Requests</p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-purple-50 rounded-lg">
                <SafeIcon icon={FiCpu} className="w-6 h-6 text-purple-600" />
              </div>
              <span className="text-sm text-green-600 font-medium">+2</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.activeModels}</p>
            <p className="text-sm text-gray-600 mt-1">Active AI Models</p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-green-50 rounded-lg">
                <SafeIcon icon={FiZap} className="w-6 h-6 text-green-600" />
              </div>
              <span className="text-sm text-green-600 font-medium">-12%</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.avgResponseTime}s</p>
            <p className="text-sm text-gray-600 mt-1">Avg Response Time</p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-orange-50 rounded-lg">
                <SafeIcon icon={FiTrendingUp} className="w-6 h-6 text-orange-600" />
              </div>
              <span className="text-sm text-green-600 font-medium">+18%</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">${stats.costSavings.toLocaleString()}</p>
            <p className="text-sm text-gray-600 mt-1">Cost Savings</p>
          </motion.div>
        </div>

        {/* Tabs */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6"
        >
          <div className="flex space-x-8 p-6 border-b border-gray-100 overflow-x-auto">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'features', label: 'AI Features' },
              { id: 'activity', label: 'Recent Activity' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'overview' && (
            <motion.div
              key="overview"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-8 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold mb-2">Welcome to AI-Powered Healthcare</h2>
                    <p className="text-indigo-100 mb-6 max-w-2xl">
                      Transform your practice with cutting-edge artificial intelligence. Automate tasks, gain insights, and provide better patient care.
                    </p>
                    <button 
                      onClick={() => setShowConfigModal(true)}
                      className="px-6 py-3 bg-white text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors font-semibold"
                    >
                      Get Started
                    </button>
                  </div>
                  <SafeIcon icon={FiCpu} className="w-32 h-32 text-indigo-300 opacity-50" />
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Usage Trends</h3>
                  <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
                    <div className="text-center">
                      <SafeIcon icon={FiBarChart2} className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">Usage chart visualization</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Model Performance</h3>
                  <div className="space-y-4">
                    {aiFeatures.filter(f => f.status === 'active').map((feature) => (
                      <div key={feature.title}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700">{feature.title}</span>
                          <span className="text-sm text-gray-600">{feature.accuracy}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              feature.color === 'blue' ? 'bg-blue-500' :
                              feature.color === 'green' ? 'bg-green-500' :
                              'bg-orange-500'
                            }`}
                            style={{ width: `${feature.accuracy}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'features' && (
            <motion.div
              key="features"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {aiFeatures.map((feature, index) => {
                  const colors = getColorClasses(feature.color);
                  const CardContent = () => (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className={`bg-white h-full flex flex-col p-6 rounded-xl shadow-sm border-2 ${colors.border} transition-all duration-300 ${
                        feature.status === 'coming-soon' ? 'cursor-default' : `cursor-pointer ${colors.hover}`
                      }`}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className={`p-3 rounded-lg ${colors.bg}`}>
                          <SafeIcon icon={feature.icon} className={`w-6 h-6 ${colors.text}`} />
                        </div>
                        {getStatusBadge(feature.status)}
                      </div>
                      
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                      <p className="text-sm text-gray-600 mb-4 flex-grow">{feature.description}</p>
                      
                      {feature.status !== 'coming-soon' && (
                        <div className="space-y-3 mb-4">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">Usage</span>
                            <span className="font-semibold text-gray-900">{feature.usage.toLocaleString()}</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">Accuracy</span>
                            <span className="font-semibold text-gray-900">{feature.accuracy}%</span>
                          </div>
                        </div>
                      )}

                      <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-200">
                        {feature.status === 'coming-soon' ? (
                          <div className="flex items-center space-x-2 text-sm font-medium text-gray-400">
                            <SafeIcon icon={FiLock} className="h-4 w-4" />
                            <span>Coming Soon</span>
                          </div>
                        ) : (
                          <div className={`flex items-center space-x-2 text-sm font-medium ${colors.text}`}>
                            <span>Explore</span>
                            <SafeIcon icon={FiArrowRight} className="h-4 w-4" />
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );

                  return feature.status === 'coming-soon' || feature.path === '#' ? (
                    <div key={feature.title}><CardContent /></div>
                  ) : (
                    <Link to={feature.path} key={feature.title} className="block">
                      <CardContent />
                    </Link>
                  );
                })}
              </div>
            </motion.div>
          )}

          {activeTab === 'activity' && (
            <motion.div
              key="activity"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-white rounded-xl shadow-sm border border-gray-100"
            >
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Recent AI Activity</h3>
                <div className="space-y-4">
                  {recentActivity.map((activity, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg"
                    >
                      <div className={`p-2 rounded-lg ${
                        activity.type === 'chat' ? 'bg-blue-100' :
                        activity.type === 'content' ? 'bg-purple-100' :
                        activity.type === 'analytics' ? 'bg-green-100' :
                        'bg-orange-100'
                      }`}>
                        <SafeIcon 
                          icon={
                            activity.type === 'chat' ? FiMessageSquare :
                            activity.type === 'content' ? FiEdit :
                            activity.type === 'analytics' ? FiBarChart2 :
                            FiTarget
                          } 
                          className={`w-5 h-5 ${
                            activity.type === 'chat' ? 'text-blue-600' :
                            activity.type === 'content' ? 'text-purple-600' :
                            activity.type === 'analytics' ? 'text-green-600' :
                            'text-orange-600'
                          }`}
                        />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{activity.user}</p>
                        <p className="text-sm text-gray-600">{activity.action}</p>
                        <p className="text-xs text-gray-400 mt-1">{activity.time}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Configuration Modal */}
        <AnimatePresence>
          {showConfigModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
              onClick={() => setShowConfigModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              >
                <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-gray-900">AI Configuration</h2>
                  <button
                    onClick={() => setShowConfigModal(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <SafeIcon icon={FiX} className="w-5 h-5 text-gray-600" />
                  </button>
                </div>

                <form onSubmit={handleConfigSave} className="p-6 space-y-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start space-x-3">
                    <SafeIcon icon={FiInfo} className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-blue-900 font-medium">Getting Started with AI</p>
                      <p className="text-sm text-blue-700 mt-1">
                        Configure your AI settings to enable intelligent features across the platform. You'll need an API key from your AI provider.
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">API Key</label>
                    <input
                      type="password"
                      name="apiKey"
                      value={aiConfig.apiKey}
                      onChange={handleConfigChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="sk-..."
                    />
                    <p className="text-xs text-gray-500 mt-1">Your API key is encrypted and stored securely</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">AI Model</label>
                    <select
                      name="model"
                      value={aiConfig.model}
                      onChange={handleConfigChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      <option value="gpt-4">GPT-4 (Most Capable)</option>
                      <option value="gpt-3.5-turbo">GPT-3.5 Turbo (Faster)</option>
                      <option value="claude-3">Claude 3</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Temperature</label>
                      <input
                        type="number"
                        name="temperature"
                        value={aiConfig.temperature}
                        onChange={handleConfigChange}
                        min="0"
                        max="2"
                        step="0.1"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">0 = Precise, 2 = Creative</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Max Tokens</label>
                      <input
                        type="number"
                        name="maxTokens"
                        value={aiConfig.maxTokens}
                        onChange={handleConfigChange}
                        min="100"
                        max="4000"
                        step="100"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-3">Enable Features</h3>
                    <div className="space-y-3">
                      <label className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          name="enableChat"
                          checked={aiConfig.enableChat}
                          onChange={handleConfigChange}
                          className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                        />
                        <span className="text-sm text-gray-700">AI Chat Assistant</span>
                      </label>
                      <label className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          name="enableAnalytics"
                          checked={aiConfig.enableAnalytics}
                          onChange={handleConfigChange}
                          className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                        />
                        <span className="text-sm text-gray-700">Analytics Insights</span>
                      </label>
                      <label className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          name="enableContent"
                          checked={aiConfig.enableContent}
                          onChange={handleConfigChange}
                          className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                        />
                        <span className="text-sm text-gray-700">Content Generation</span>
                      </label>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => setShowConfigModal(false)}
                      className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center space-x-2"
                    >
                      <SafeIcon icon={FiCheck} className="w-4 h-4" />
                      <span>Save Configuration</span>
                    </button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default AIIntegration;